/**
 * FloorToolBase - Shared functionality for floor-related tools
 *
 * Base class providing common functionality for:
 * - AutoFloorTool: Automatic floor detection
 * - FloorSelectorTool: Manual floor selection from objects tab
 */

import { BaseTool, DragEventData } from '../../BaseTool';
import { DEFAULT_SLAB_THICKNESS_M, SLAB_SURFACE_MARGIN_M } from '@pointorama/potree-shared';
import { DetectedFloorPlane, DetectedFloorLevel } from '../../../utils/floorDetection';
import { FloorBarsOverlay, isFloorBarsEvent } from '../../../utils/FloorBarsOverlay';
import { FloorPreviewEditRig, FloorRigFace, FACE_REPICK_EXTRUDE_M } from '../modules/FloorPreviewEditRig';
import { defaultFloorIsolationStrategy } from '../../../utils/FloorIsolation';
import { CameraCullSphere } from '../modules/CameraCullSphere';
import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Utils } from '../../../utils';

export abstract class FloorToolBase extends BaseTool {
  // Detection results
  protected detectedFloors: DetectedFloorPlane[] = []; // All detected planes for visualization
  protected availableFloors: DetectedFloorLevel[] = []; // Floor/ceiling pairs that haven't been selected yet
  // Levels confirmed this run: kept on the elevation map (green, proposals grey).
  protected addedFloors: DetectedFloorLevel[] = [];
  // An added level shown again by clicking its bar (-1 = none). A preview
  // like a proposal's, but it can't be edited or added again.
  protected previewedAddedIndex = -1;

  // UI elements for 3D view
  protected floorVisualizationGroup: THREE.Group | null = null;
  protected floorMeshes: THREE.Mesh[] = [];
  protected hoveredFloorIndex = -1;


  // Left-edge elevation map of the proposed levels (bars mirror 3D hover/click)
  protected floorBarsOverlay: FloorBarsOverlay | null = null;

  // Preview state (first click - for inspection)
  protected previewFloorIndex = -1;

  // Edit rig on the previewed box: side drag handles + top/bottom plane repick
  protected previewEditRig: FloorPreviewEditRig | null = null;
  /** Camera cull bubble during a face repick — lets the user look into the
   *  rooms without the old camera-sector half-box section cut. */
  private readonly repickCameraCull = new CameraCullSphere(this.viewer);

  // Preview outline visualization (green outline around floor volume)
  protected previewOutlineGroup: THREE.Group | null = null;
  protected previewOutlineLines: Line2[] = [];

  // Detection area corners (user-drawn selection area, used for visualization sizing)
  // 4 XY corner points; Z comes from scene bounds or floor heights
  protected detectionAreaCorners: THREE.Vector3[] | null = null;

  // Raycaster for selection
  protected raycaster: THREE.Raycaster = new THREE.Raycaster();
  protected mouse: THREE.Vector2 = new THREE.Vector2();

  // Track last confirmation time to prevent double-click from starting new preview
  private lastConfirmTime = 0;

  // Store previous double-click zoom state to restore on disable
  private previousDoubleClickZoomState = true;

  /**
   * Get bounding box for visualization sizing.
   * Uses detection area corners if available, otherwise falls back to scene bounds.
   */
  protected getVisualizationBounds(): THREE.Box3 {
    if (this.detectionAreaCorners && this.detectionAreaCorners.length >= 4) {
      const box = new THREE.Box3();
      for (const corner of this.detectionAreaCorners) {
        box.expandByPoint(corner);
      }
      return box;
    }
    return this.viewer.getBoundingBox(this.viewer.scene.pointclouds);
  }

  /**
   * Get the 4 XY corner points for floor visualization footprint.
   * Uses detection area corners if available (supports off-axis rectangles),
   * otherwise derives corners from the axis-aligned scene bounding box.
   * Returns corners in order: [bottom-left, bottom-right, top-right, top-left] in XY.
   */
  protected getVisualizationCorners(): THREE.Vector2[] {
    if (this.detectionAreaCorners && this.detectionAreaCorners.length >= 4) {
      return this.detectionAreaCorners.map((c) => new THREE.Vector2(c.x, c.y));
    }
    const bb = this.viewer.getBoundingBox(this.viewer.scene.pointclouds);
    return [new THREE.Vector2(bb.min.x, bb.min.y), new THREE.Vector2(bb.max.x, bb.min.y), new THREE.Vector2(bb.max.x, bb.max.y), new THREE.Vector2(bb.min.x, bb.max.y)];
  }

  /** XY corners of an axis-aligned box, in visualization corner order. */
  private static boxCorners(box: THREE.Box3): THREE.Vector2[] {
    return [new THREE.Vector2(box.min.x, box.min.y), new THREE.Vector2(box.max.x, box.min.y), new THREE.Vector2(box.max.x, box.max.y), new THREE.Vector2(box.min.x, box.max.y)];
  }

  /**
   * A level's own footprint corners: persisted per-floor corners first (may
   * be rotated), then the detection footprint (union of room patches), then
   * the global detection rect / scene bounds as the legacy fallback.
   */
  protected getLevelCorners(level: DetectedFloorLevel): THREE.Vector2[] {
    if (level.footprintCorners && level.footprintCorners.length >= 4) return level.footprintCorners;
    if (level.footprint) return FloorToolBase.boxCorners(level.footprint);
    return this.getVisualizationCorners();
  }

  protected onEnable(): void {
    // Reset state
    this.lastConfirmTime = 0;

    // Disable orbital double-click zoom
    if (this.viewer.orbitControls) {
      this.previousDoubleClickZoomState = this.viewer.orbitControls.doubleClickZoomEnabled;
      this.viewer.orbitControls.doubleClickZoomEnabled = false;
    }

    // Listen for objects changed to cancel preview if floor is deleted
    this.viewer.scene.addEventListener('objects_changed', this.onObjectsChanged);
  }

  protected onDisable(): void {
    // Remove listeners
    this.viewer.scene.removeEventListener('objects_changed', this.onObjectsChanged);

    this.repickCameraCull.disable();

    // Clean up UI elements
    this.clearFloorVisualization();
    this.clearPreviewOutline();
    this.clearBoxIsolation();
    this.onCleanup(); // Let subclasses clean up their specific resources

    // Reset detection area
    this.detectionAreaCorners = null;

    // Reset cursor
    this.viewer.renderer.domElement.style.cursor = 'default';

    // Restore orbital double-click zoom to its previous state
    if (this.viewer.orbitControls) {
      this.viewer.orbitControls.doubleClickZoomEnabled = this.previousDoubleClickZoomState;
    }
  }

  /**
   * Handle objects changed event - cancel preview if selected floor no longer exists
   */
  private onObjectsChanged = (event: { groups: { type: string }[] }): void => {
    const groups = event.groups || [];
    // Get current floor groups from the event
    const currentFloorGroups = groups.filter((group) => group.type === 'FLOOR');

    // If the previewed floor was deleted, cancel the preview
    if (this.previewFloorIndex !== -1 && currentFloorGroups.length === 0) {
      this.cancelPreview();
    }
  };

  /**
   * Create 3D visualization for available floors only
   * Shows volumes between floors and their ceilings from floor levels
   */
  protected createFloorVisualization(): void {
    this.clearFloorVisualization();

    const group = new THREE.Group();
    group.name = 'floor-tool-3d-visualization';

    // Create a volume mesh for each available floor level
    for (let i = 0; i < this.availableFloors.length; i++) {
      const floorLevel = this.availableFloors[i];

      if (!floorLevel.ceiling) {
        // Skip floor if it has no ceiling
        continue;
      }

      const floor = floorLevel.floor;
      const ceiling = floorLevel.ceiling;
      const floorHeight = ceiling.height - floor.height;

      // The level's OWN footprint (a mezzanine covers only its patch union,
      // not the whole detection rect).
      const xyCorners = this.getLevelCorners(floorLevel);
      const centerX = xyCorners.reduce((s, c) => s + c.x, 0) / xyCorners.length;
      const centerY = xyCorners.reduce((s, c) => s + c.y, 0) / xyCorners.length;

      // Build a Shape from the 4 XY corners (local coords relative to center)
      const shape = new THREE.Shape();
      shape.moveTo(xyCorners[0].x - centerX, xyCorners[0].y - centerY);
      for (let j = 1; j < xyCorners.length; j++) {
        shape.lineTo(xyCorners[j].x - centerX, xyCorners[j].y - centerY);
      }
      shape.closePath();

      // Extrude along Z by the floor height
      const extrudeGeometry = new THREE.ExtrudeGeometry(shape, {
        depth: floorHeight,
        bevelEnabled: false,
      });

      const boxMaterial = new THREE.MeshBasicMaterial({
        color: 0xb5ffa1, // Brand green
        transparent: true,
        opacity: 0.05,
        side: THREE.DoubleSide,
        depthWrite: false,
      });

      const floorMesh = new THREE.Mesh(extrudeGeometry, boxMaterial);
      // ExtrudeGeometry extrudes Shape (XY plane) along Z.
      // Position: center XY, floor.height for Z base
      floorMesh.position.set(centerX, centerY, floor.height);
      floorMesh.userData = {
        floorIndex: i,
        floorLevel: floorLevel,
        isFloorVolume: true,
      };

      this.floorMeshes.push(floorMesh);
      group.add(floorMesh);
    }

    this.floorVisualizationGroup = group;
    this.viewer.scene.scene.add(group);

    // Elevation-map bars on the left edge, mirroring the 3D volumes' hover
    // and click behavior.
    if (!this.floorBarsOverlay) {
      // Floor tooling proper: the panel opens by default here (selection
      // tools start it minimized).
      this.floorBarsOverlay = new FloorBarsOverlay(
        this.viewer.renderArea,
        {
          onHover: (floorIndex) => this.onFloorBarHovered(floorIndex),
          onClick: (floorIndex) => this.onFloorBarClicked(floorIndex),
          onDoubleClick: (floorIndex) => this.onFloorBarDoubleClicked(floorIndex),
          onAddedClick: (addedIndex) => this.onAddedFloorBarClicked(addedIndex),
        },
        { defaultCollapsed: false, mutedProposals: this.mutesProposalBars() }
      );
    }
    this.floorBarsOverlay.setLevels(this.availableFloors, this.addedFloors);

    // Push the resting-state isolation clip via the floor isolation manager.
    this.isolateDefault();
  }

  /** Bar hover mirrors hovering the level volume in 3D (ignored during preview, like handleHover). */
  protected onFloorBarHovered(floorIndex: number | null): void {
    if (this.isPreviewing()) return;
    const next = floorIndex ?? -1;
    if (this.hoveredFloorIndex === next) return;
    this.hoveredFloorIndex = next;
    this.updateFloorVisualization();
  }

  /**
   * Bar click previews the level. Clicking the previewed bar again does
   * nothing: adding takes a real double click (onFloorBarDoubleClicked), so a
   * stray second click later never adds a floor.
   */
  protected onFloorBarClicked(floorIndex: number): void {
    // A bar click is a deliberate floor action — exit any plane repick first.
    this.previewEditRig?.cancelRepick();
    if (this.previewFloorIndex === floorIndex) return;
    this.previewFloor(floorIndex);
  }

  /**
   * An added level's bar clicked: show that level's preview again (its
   * isolation and outline), with its bar highlighted. No edit rig, and Enter
   * or a double click never adds it again.
   */
  protected onAddedFloorBarClicked(addedIndex: number): void {
    const level = this.addedFloors[addedIndex];
    if (!level || this.previewedAddedIndex === addedIndex) return;
    this.previewEditRig?.cancelRepick();
    if (this.previewFloorIndex !== -1) this.cancelPreview();

    if (this.floorVisualizationGroup) this.floorVisualizationGroup.visible = false;
    this.previewedAddedIndex = addedIndex;
    this.floorBarsOverlay?.setPreviewedAdded(addedIndex);
    this.hoveredFloorIndex = -1;
    this.floorBarsOverlay?.setHovered(-1);
    this.previewEditRig?.hide();

    if (level.ceiling) this.isolateFloor(level.floor, level.ceiling, this.getLevelCorners(level));
    this.clearPreviewOutline();
    this.createPreviewOutline(level);
  }

  /** Whether a level is in preview: a proposal, or an added level shown again. */
  protected isPreviewing(): boolean {
    return this.previewFloorIndex !== -1 || this.previewedAddedIndex !== -1;
  }

  /** Bar double click. Nothing by default; tools that add floors confirm the level. */
  protected onFloorBarDoubleClicked(_floorIndex: number): void {
    // Default: do nothing.
  }

  /**
   * Clear floor visualization
   */
  protected clearFloorVisualization(): void {
    if (this.floorVisualizationGroup) {
      this.viewer.scene.scene.remove(this.floorVisualizationGroup);
      this.floorVisualizationGroup.traverse((child) => {
        const mesh = child as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material && !Array.isArray(mesh.material)) mesh.material.dispose();
      });
      this.floorVisualizationGroup = null;
    }
    this.floorMeshes = [];
    this.hoveredFloorIndex = -1;
    this.floorBarsOverlay?.clear();
    this.previewEditRig?.hide();
    this.clearBoxIsolation();
  }

  /**
   * Update floor visualization based on hover state
   */
  protected updateFloorVisualization(): void {
    // Update mesh opacities
    this.floorMeshes.forEach((mesh) => {
      const floorIndex = mesh.userData.floorIndex as number;
      if (mesh.material instanceof THREE.MeshBasicMaterial) {
        if (floorIndex === this.hoveredFloorIndex) {
          mesh.material.opacity = 0.4;
          mesh.material.color.set(0xb5ffa1);
        } else if (this.hoveredFloorIndex !== -1) {
          mesh.material.opacity = 0.01;
        } else {
          mesh.material.opacity = 0.05;
          mesh.material.color.set(0xb5ffa1);
        }
      }
    });


    // Mirror the hover on the elevation-map bars.
    this.floorBarsOverlay?.setHovered(this.hoveredFloorIndex);

    // Reshape the clip: hovered level's slab over its own footprint (same
    // behavior as hovering the elevation-map bar), else the resting state.
    if (this.hoveredFloorIndex !== -1 && this.hoveredFloorIndex < this.availableFloors.length) {
      const floorLevel = this.availableFloors[this.hoveredFloorIndex];
      if (floorLevel.ceiling) {
        this.isolateFloor(floorLevel.floor, floorLevel.ceiling, this.getLevelCorners(floorLevel));
      }
    } else {
      this.isolateDefault();
    }
  }


  /**
   * Push a box-shaped clipping strategy via the floor isolation manager.
   * The XY footprint defaults to the detection area (or scene-bounds
   * fallback); callers may pass tighter corners (level footprint, room
   * bounds). Z is supplied by the caller.
   */
  private applyBoxIsolation(minZ: number, maxZ: number, corners?: THREE.Vector2[]): void {
    const xy = corners ?? this.getVisualizationCorners();
    if (xy.length < 4) return;
    this.viewer.floorIsolationManager.setCustomStrategy({
      kind: 'box',
      xyCorners: [xy[0], xy[1], xy[2], xy[3]],
      minZ,
      maxZ,
    });
  }

  /** Push the default fixed-height isolation clip for a floor group. */
  protected applyFixedHeightIsolation(floorGroupId: string): void {
    this.viewer.floorIsolationManager.setCustomStrategy(defaultFloorIsolationStrategy(floorGroupId));
  }

  /** Isolate a hovered/previewed floor slab. Overridable; default is a box clip. */
  protected isolateFloor(floor: DetectedFloorPlane, ceiling: DetectedFloorPlane, corners?: THREE.Vector2[]): void {
    // Same slab-surface margin as the room tooling: the planes are fitted
    // THROUGH the slab scatter, so an exact clip would cut roughly half the
    // floor/ceiling surface points.
    this.applyBoxIsolation(floor.height - SLAB_SURFACE_MARGIN_M, ceiling.height + SLAB_SURFACE_MARGIN_M, corners);
  }

  /** Resting-state isolation when nothing is hovered/previewed. Overridable; default is the all-floors box. */
  protected isolateDefault(): void {
    this.applyAllFloorsBoxIsolation();
  }

  /**
   * Push a box clip covering all currently-available floors (Z spans the
   * lowest floor to the highest TRUE ceiling, plus a slab allowance so the
   * roof slab above the top level stays visible). Falls back to the scene
   * bounds when no floors remain so the rectangle XY still constrains
   * visibility.
   */
  protected applyAllFloorsBoxIsolation(): void {
    let minZ: number;
    let maxZ: number;
    let corners: THREE.Vector2[] | undefined;

    if (this.availableFloors.length > 0) {
      // Slab-surface margin below the lowest floor (the plane is fitted
      // THROUGH the slab scatter); the top already carries a slab allowance.
      minZ = Math.min(...this.availableFloors.map((f) => f.floor.height)) - SLAB_SURFACE_MARGIN_M;
      maxZ = Math.max(...this.availableFloors.map((f) => f.ceiling?.height ?? f.floor.height)) + DEFAULT_SLAB_THICKNESS_M;

      // XY: union of the levels' own footprints when every level has one;
      // otherwise the global detection rect / scene bounds fallback.
      if (this.availableFloors.every((f) => f.footprintCorners || f.footprint)) {
        const union = new THREE.Box3();
        for (const level of this.availableFloors) {
          for (const corner of this.getLevelCorners(level)) {
            union.expandByPoint(new THREE.Vector3(corner.x, corner.y, minZ));
          }
        }
        corners = FloorToolBase.boxCorners(union);
      }
    } else {
      const sceneBounds = this.viewer.getBoundingBox(this.viewer.scene.pointclouds);
      minZ = sceneBounds.min.z;
      maxZ = sceneBounds.max.z;
    }

    this.applyBoxIsolation(minZ, maxZ, corners);
  }

  /**
   * Drop the tool-driven box clip from the manager.
   */
  protected clearBoxIsolation(): void {
    this.viewer.floorIsolationManager.clearCustomStrategy();
  }

  /**
   * Create green outline around the preview floor volume
   * @param floorLevel - The floor level to outline
   */
  protected createPreviewOutline(floorLevel: DetectedFloorLevel): void {
    this.clearPreviewOutline();

    if (!floorLevel.ceiling) return;

    const group = new THREE.Group();
    group.name = 'floor-preview-outline';

    // The level's own footprint (falls back to the detection rect, which also
    // supports off-axis rectangles)
    const xyCorners = this.getLevelCorners(floorLevel);

    // Compute center for floating-point precision
    const centerX = xyCorners.reduce((s, c) => s + c.x, 0) / xyCorners.length;
    const centerY = xyCorners.reduce((s, c) => s + c.y, 0) / xyCorners.length;

    const floor = floorLevel.floor;
    const ceiling = floorLevel.ceiling;
    const centerZ = (floor.height + ceiling.height) / 2;

    // CRITICAL: Position group at center to use local coordinates
    // This prevents floating-point precision issues at high elevations (400m-800m)
    // Same technique used in Measure.ts and PointoramaRoom.ts
    group.position.set(centerX, centerY, centerZ);

    const minZ = floor.height - centerZ;
    const maxZ = ceiling.height - centerZ;

    // Build 8 corners from the 4 XY corners at floor and ceiling heights (local coords)
    const corners = [
      // Bottom face corners (at floor height)
      ...xyCorners.map((c) => new THREE.Vector3(c.x - centerX, c.y - centerY, minZ)),
      // Top face corners (at ceiling height)
      ...xyCorners.map((c) => new THREE.Vector3(c.x - centerX, c.y - centerY, maxZ)),
    ];

    // Define the 12 edges of the box as pairs of corner indices
    // Bottom: 0-1, 1-2, 2-3, 3-0 | Top: 4-5, 5-6, 6-7, 7-4 | Verticals: 0-4, 1-5, 2-6, 3-7
    const edges = [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 0],
      [4, 5],
      [5, 6],
      [6, 7],
      [7, 4],
      [0, 4],
      [1, 5],
      [2, 6],
      [3, 7],
    ];

    // Create line material with green color
    const lineMaterial = new LineMaterial({
      color: 0x59cd90, // Brand green
      linewidth: 3,
      resolution: new THREE.Vector2(this.viewer.renderer.domElement.clientWidth, this.viewer.renderer.domElement.clientHeight),
      depthTest: false,
      transparent: true,
      opacity: 0.9,
    });

    // Create each edge as a Line2 using local coordinates
    for (const [startIdx, endIdx] of edges) {
      const start = corners[startIdx];
      const end = corners[endIdx];

      const lineGeometry = new LineGeometry();
      lineGeometry.setPositions([start.x, start.y, start.z, end.x, end.y, end.z]);

      const line = new Line2(lineGeometry, lineMaterial.clone());
      line.computeLineDistances();
      this.previewOutlineLines.push(line);
      group.add(line);
    }

    this.previewOutlineGroup = group;
    this.viewer.measurementScene.add(group);
  }

  /**
   * Clear preview outline
   */
  protected clearPreviewOutline(): void {
    if (this.previewOutlineGroup) {
      this.viewer.measurementScene.remove(this.previewOutlineGroup);

      // Dispose of geometries and materials
      for (const line of this.previewOutlineLines) {
        line.geometry.dispose();
        if (line.material instanceof LineMaterial) {
          line.material.dispose();
        }
      }

      this.previewOutlineGroup = null;
      this.previewOutlineLines = [];
    }
  }

  /**
   * Hook for subclasses to clean up their specific resources
   * Override in subclasses that need cleanup (e.g., AutoFloorTool clearing clipping planes)
   */
  protected onCleanup(): void {
    // Default: do nothing
    // Subclasses can override to clean up clipping planes, etc.
  }

  /**
   * Preview a floor (first click)
   * @param floorIndex - Index in availableFloors array
   * @param floor - Optional floor plane. If not provided, uses availableFloors[floorIndex].floor
   * @param ceiling - Optional ceiling plane. If not provided, uses availableFloors[floorIndex].ceiling
   */
  protected previewFloor(floorIndex: number, floor?: DetectedFloorPlane, ceiling?: DetectedFloorPlane): void {
    if (floorIndex < 0 || floorIndex >= this.availableFloors.length) {
      return;
    }

    if (this.floorVisualizationGroup) {
      this.floorVisualizationGroup.visible = false;
    }

    this.previewFloorIndex = floorIndex;
    this.floorBarsOverlay?.setPreviewed(floorIndex);
    this.previewedAddedIndex = -1;
    this.floorBarsOverlay?.setPreviewedAdded(-1);

    // Hover is suppressed while previewing (handleHover / onFloorBarHovered
    // early-return), so drop any hover state left from before the click —
    // otherwise a previous bar keeps its highlight instead of dimming.
    this.hoveredFloorIndex = -1;
    this.floorBarsOverlay?.setHovered(-1);

    // Get floor level
    const floorLevel = this.availableFloors[floorIndex];
    const floorPlane = floor || floorLevel.floor;
    const ceilingPlane = ceiling || floorLevel.ceiling;

    // Reshape the clip to this floor's slab (over the level's own footprint).
    if (ceilingPlane) {
      this.isolateFloor(floorPlane, ceilingPlane, this.getLevelCorners(floorLevel));
    }

    // Create green outline around the preview floor volume
    // Create a temporary floor level with the actual planes being used
    const previewFloorLevel: DetectedFloorLevel = {
      floor: floorPlane,
      ceiling: ceilingPlane || null,
      footprint: floorLevel.footprint,
      footprintCorners: floorLevel.footprintCorners,
    };
    this.createPreviewOutline(previewFloorLevel);

    // Edit rig: side drag handles + top/bottom plane repick on the preview box
    if (ceilingPlane && this.allowsPreviewEditing()) {
      if (!this.previewEditRig) {
        this.previewEditRig = new FloorPreviewEditRig(this.viewer, {
          onFootprintChanged: (corners, committed) => this.handleRigFootprintChanged(corners, committed),
          onFacePlanePicked: (face, z) => this.handleRigFacePlanePicked(face, z),
          onRepickChanged: (face) => this.handleRigRepickChanged(face),
        });
      }
      this.previewEditRig.show(this.getLevelCorners(floorLevel), floorPlane.height, ceilingPlane.height);
    }
  }

  /**
   * Whether the elevation map draws proposals grey so added levels (green)
   * stand out. Only the flows that add floors from proposals turn it on.
   */
  protected mutesProposalBars(): boolean {
    return false;
  }

  /**
   * Whether the previewed box gets the edit rig (side resize arrows +
   * top/bottom plane repick). Selection tools override to `false` — editing
   * belongs to the creation flows (auto proposals, manual floor tool).
   */
  protected allowsPreviewEditing(): boolean {
    return true;
  }

  /** The level currently in preview, or null. */
  private previewedLevel(): DetectedFloorLevel | null {
    return this.availableFloors[this.previewFloorIndex] ?? null;
  }

  /** Live/committed footprint change from a side-handle drag. */
  protected handleRigFootprintChanged(corners: THREE.Vector2[], committed: boolean): void {
    const level = this.previewedLevel();
    if (!level) return;
    level.footprintCorners = corners;
    if (level.footprint) {
      // Keep the AABB variant in sync for consumers that read it.
      const box = new THREE.Box3();
      for (const c of corners) {
        box.expandByPoint(new THREE.Vector3(c.x, c.y, level.floor.height));
        box.expandByPoint(new THREE.Vector3(c.x, c.y, level.ceiling?.height ?? level.floor.height));
      }
      level.footprint.copy(box);
    }

    if (committed) {
      this.onPreviewLevelEdited(level);
      const index = this.previewFloorIndex;
      this.createFloorVisualization();
      this.previewFloor(index);
    } else if (level.ceiling) {
      // Live: follow the drag with outline + clip only (full rebuild on release).
      this.isolateFloor(level.floor, level.ceiling, corners);
      this.createPreviewOutline(level);
    }
  }

  /** A new floor/ceiling plane height was picked on the top/bottom face. */
  protected handleRigFacePlanePicked(face: FloorRigFace, z: number): void {
    const level = this.previewedLevel();
    if (!level || !level.ceiling) return;

    // Clamp so the level can never invert or collapse.
    const minInterior = 0.5;
    const plane = face === 'bottom' ? level.floor : level.ceiling;
    const clampedZ = face === 'bottom' ? Math.min(z, level.ceiling.height - minInterior) : Math.max(z, level.floor.height + minInterior);

    plane.height = clampedZ;
    plane.center.z = clampedZ;
    if (plane.bounds) {
      plane.bounds.min.z = clampedZ;
      plane.bounds.max.z = clampedZ;
    }
    if (level.footprint) {
      level.footprint.min.z = level.floor.height;
      level.footprint.max.z = level.ceiling.height;
    }

    this.onPreviewLevelEdited(level);
    const index = this.previewFloorIndex;
    this.createFloorVisualization();
    this.previewFloor(index);
  }

  /**
   * Repick entered/exited: extrude the isolation past the picked face, and
   * cull the cloud around the camera so the user can look INTO the rooms
   * (replaces the old camera-sector half-box section cut).
   */
  protected handleRigRepickChanged(face: FloorRigFace | null): void {
    // Release the cull before any early return so it can't outlive a repick
    // whose previewed level disappeared mid-flight.
    if (!face) this.repickCameraCull.disable();
    const level = this.previewedLevel();
    if (!level || !level.ceiling) return;
    const corners = this.getLevelCorners(level);
    // While repicking, all other preview visuals get out of the way — only
    // the pick-plane preview should be in view.
    if (this.previewOutlineGroup) this.previewOutlineGroup.visible = face === null;
    if (face) {
      const minZ = level.floor.height - SLAB_SURFACE_MARGIN_M - (face === 'bottom' ? FACE_REPICK_EXTRUDE_M : 0);
      const maxZ = level.ceiling.height + SLAB_SURFACE_MARGIN_M + (face === 'top' ? FACE_REPICK_EXTRUDE_M : 0);
      this.applyBoxIsolation(minZ, maxZ, corners);
      this.repickCameraCull.enable();
    } else {
      this.isolateFloor(level.floor, level.ceiling, corners);
    }
  }

  /**
   * A previewed level's planes or footprint were edited via the rig. Default:
   * nothing — detection proposals persist when confirmed. FloorSelectorTool
   * overrides to write the persisted floor group's reactive properties.
   */
  protected onPreviewLevelEdited(_level: DetectedFloorLevel): void {
    // Default: in-memory only.
  }

  /**
   * Cancel preview
   */
  protected cancelPreview(): void {
    if (this.floorVisualizationGroup) {
      this.floorVisualizationGroup.visible = true;
    }
    this.previewFloorIndex = -1;
    this.floorBarsOverlay?.setPreviewed(-1);
    this.previewedAddedIndex = -1;
    this.floorBarsOverlay?.setPreviewedAdded(-1);
    this.previewEditRig?.hide();

    // Restore the resting-state isolation and clear the outline.
    this.isolateDefault();
    this.clearPreviewOutline();
  }

  /**
   * Confirm floor selection
   */
  protected confirmFloorSelection(floorIndex: number): void {
    if (floorIndex < 0 || floorIndex >= this.availableFloors.length) {
      return;
    }

    this.lastConfirmTime = Date.now();
    const selectedFloorLevel = this.availableFloors[floorIndex];

    this.previewEditRig?.hide();
    this.clearPreviewOutline();

    // Move the floor from the proposals to the added levels (kept on the map)
    this.availableFloors.splice(floorIndex, 1);
    this.addedFloors.push(selectedFloorLevel);

    this.hoveredFloorIndex = -1;
    this.previewFloorIndex = -1;
    this.previewedAddedIndex = -1;

    // Recompute the all-floors box (now excludes the just-confirmed floor).
    this.createFloorVisualization();

    if (this.floorVisualizationGroup) {
      this.floorVisualizationGroup.visible = true;
    }

    // Get floor and ceiling from the selected floor level
    const selectedFloor = selectedFloorLevel.floor;
    const ceiling = selectedFloorLevel.ceiling;

    const ceilingData = ceiling
      ? {
          ...ceiling,
          normal: ceiling.normal.clone().negate(),
        }
      : undefined;

    // Call hook for subclass-specific behavior (e.g., floor group creation, clipping, notifications)
    this.onFloorConfirmed(selectedFloor, ceilingData, this.availableFloors.length, this.getLevelCorners(selectedFloorLevel));
  }

  /**
   * Hook called after a floor is confirmed
   * Override in subclasses to add custom behavior (e.g., create floor group, send notifications)
   * @param _floor - The confirmed floor
   * @param _ceiling - The ceiling data (if available)
   * @param _floorsRemaining - Number of floors still available
   * @param _footprintCorners - The confirmed level's own XY footprint corners
   */
  protected onFloorConfirmed(_floor: DetectedFloorPlane, _ceiling: DetectedFloorPlane | undefined, _floorsRemaining: number, _footprintCorners: THREE.Vector2[]): void {
    // Default: do nothing
    // Subclasses override to dispatch create_floor event, etc.
  }

  // Mouse event handlers (public to match ITool interface)
  public onClick(event: MouseEvent): void {
    // BaseTool handles drag filtering automatically
    const timeSinceConfirm = Date.now() - this.lastConfirmTime;
    if (timeSinceConfirm < 500) {
      return;
    }

    this.handleFloorClick(event);
  }

  public onMouseDown(event: MouseEvent): void {
    if (event.button !== 0) return;
    if (this.previewFloorIndex !== -1) this.previewEditRig?.onMouseDown(event);
  }

  public onDragStart(data: DragEventData): void {
    this.previewEditRig?.onDragStart(data);
  }

  public onDrag(data: DragEventData): void {
    this.previewEditRig?.onDrag(data);
  }

  public onDragEnd(data: DragEventData): void {
    this.previewEditRig?.onDragEnd(data);
  }

  /**
   * Rig interactions for a click: handle release, repick commit, or entering
   * repick via the top/bottom face. The face wins when the visible cloud
   * point under the cursor lies NEAR the face's z-plane (the user is clicking
   * that floor/ceiling surface) or when there is none; a cloud hit far from
   * the plane means the click aims at a wall / the interior and belongs to
   * the confirm/cancel flow.
   */
  protected handleRigClick(event: MouseEvent): boolean {
    const rig = this.previewEditRig;
    if (!rig) return false;
    if (rig.onClick(event)) return true;
    if (this.previewFloorIndex === -1) return false;

    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    const mousePixel = new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
    const camera = this.viewer.scene.getActiveCamera();
    const intersection = Utils.getMousePointCloudIntersection(mousePixel, camera, this.viewer, this.viewer.scene.pointclouds, {
      pickClipped: false,
      all: false,
    });
    const cloudZ = intersection && !Array.isArray(intersection) ? intersection.location.z : null;

    const face = rig.resolveFacePick(event, cloudZ);
    if (!face) return false;

    rig.enterRepick(face);
    return true;
  }

  public onDoubleClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  public onMouseMove(event: MouseEvent): void {
    this.handleHover(event);
  }

  public onRightClick(event: MouseEvent): void {
    event.preventDefault();
    // Right click first exits an active plane repick, then cancels preview
    if (this.previewEditRig?.onRightClick(event)) return;
    this.cancelPreview();
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && this.previewFloorIndex !== -1 && !this.previewEditRig?.repickFace) {
      event.preventDefault();
      this.confirmFloorSelection(this.previewFloorIndex);
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      if (this.previewEditRig?.cancelRepick()) {
        // Exit plane repick first; preview stays active
        return;
      }
      if (this.isPreviewing()) {
        // Cancel preview if active
        this.cancelPreview();
      } else {
        // Switch back to default tool (floor_selector)
        this.persona.enableTool('floor_selector');
      }
    }
  }

  protected handleFloorClick(event: MouseEvent): void {
    if (this.handleRigClick(event)) return;

    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    const camera = this.viewer.scene.getActiveCamera();
    this.raycaster.setFromCamera(this.mouse, camera);

    // In preview mode a click never adds the level (that takes Enter, or a
    // double click on its bar): on the visible cloud it does nothing, on empty
    // space it cancels the preview.
    if (this.isPreviewing()) {
      // Check if we hit any visible (non-clipped) point cloud
      // Use Utils.getMousePointCloudIntersection with pickClipped: false to ignore hidden points
      // Convert to pixel coordinates (Utils expects pixel coords, not NDC)
      const mousePixel = new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
      const intersection = Utils.getMousePointCloudIntersection(mousePixel, camera, this.viewer, this.viewer.scene.pointclouds, {
        pickClipped: false, // Ignore clipped/hidden points
        all: false,
      });

      if (!intersection) this.cancelPreview();
      return;
    }

    const intersects = this.raycaster.intersectObjects(this.floorMeshes, false);

    if (intersects.length > 0) {
      const floorIndex = intersects[0].object.userData.floorIndex;

      if (floorIndex !== undefined) {
        this.previewFloor(floorIndex);
      }
    }
  }

  protected handleHover(event: MouseEvent): void {
    if (this.isPreviewing()) {
      const rig = this.previewEditRig;
      const consumed = rig?.onMouseMove(event) ?? false;
      // Nothing to click in the cloud while previewing: no pointer cursor.
      this.viewer.renderer.domElement.style.cursor = rig?.repickFace ? 'crosshair' : consumed ? 'move' : 'default';
      return;
    }

    // Moves over the elevation-map bars are handled by the overlay's own
    // enter/leave events — the 3D raycast must not fight them.
    if (isFloorBarsEvent(event)) return;

    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    const camera = this.viewer.scene.getActiveCamera();
    this.raycaster.setFromCamera(this.mouse, camera);

    const previousHoveredIndex = this.hoveredFloorIndex;

    const intersects = this.raycaster.intersectObjects(this.floorMeshes, false);
    if (intersects.length > 0) {
      const floorIndex = intersects[0].object.userData.floorIndex;
      this.hoveredFloorIndex = floorIndex;
      this.viewer.renderer.domElement.style.cursor = 'pointer';
    } else {
      this.hoveredFloorIndex = -1;
      this.viewer.renderer.domElement.style.cursor = 'default';
    }

    if (previousHoveredIndex !== this.hoveredFloorIndex) {
      this.updateFloorVisualization();
    }
  }

  /**
   * Get all detected floors
   */
  getDetectedFloors(): DetectedFloorPlane[] {
    return this.detectedFloors;
  }

  /**
   * Get available floor levels
   */
  getAvailableFloors(): DetectedFloorLevel[] {
    return this.availableFloors;
  }
}
